<script setup lang="ts" generic="ModelValueType extends string | number | boolean | undefined">
import UiRadioButton from '@/components/UIElements/UiRadio/UiRadioButton.vue';
import type { ElRadioGroup } from 'element-plus';

interface OptionsParams {
  label: string;
  value?: ModelValueType;
  disabled?: boolean;
}

interface Props {
  label?: string,
  hint?: string,
  disabled?: boolean,
  buttonClassName?: string,
  groupClassName?: string,
  direction?: 'row' | 'col',
  columns?: 1 | 2,
  options?: OptionsParams[],
}

interface Emits<T> {
  'change': [value: T],
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  hint: '',
  direction: 'row',
  columns: 1,
  buttonClassName: '',
  groupClassName: '',
  disabled: false,
  options: () => [],
});

const emit = defineEmits<Emits<ModelValueType>>();
const modelValue = defineModel<ModelValueType>();

const handleChange = async (value: ModelValueType) => {
  emit('change', value);
};
</script>

<template>
  <div
    :class="[
      'ui-radio-group t:space-y-8',
      `ui-radio-group_${direction}`,
    ]"
  >
    <UiLabel
      v-if="label"
      :disabled="disabled"
      :label="label"
      :hint="hint"
    />
    <ElRadioGroup
      v-model="modelValue"
      :disabled="disabled"
      :class="[
        'ui-radio-group__radio-group ',
        { 't:space-y-8': columns !== 2 && direction === 'col' },
        { 't:space-x-16': columns !== 2 && direction === 'row' },
        { 'ui-radio-group__radio-group_columns-2': columns === 2 },
        groupClassName
      ]"
      @change="handleChange($event as ModelValueType)"
    >
      <slot name="buttons">
        <UiRadioButton
          v-for="(opt, id) in options"
          :key="id"
          :value="opt.value"
          :text="opt.label"
          :disabled="disabled || opt.disabled"
          :class="buttonClassName"
        />
      </slot>
    </ElRadioGroup>
    <slot />
  </div>
</template>

<style lang="scss">
.ui-radio-group {
  display: flex;
  flex-direction: column;
  line-height: normal;
  &_col &{
    &__radio-group{
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__radio-group_columns-2 {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 30px;
  }
}
</style>
